// The Friday review, three steps: how this week went, next week's proposal (tick what you'll
// ride), then book it into the diary. The Friday 17:00 notice opens here.
import { useEffect, useState } from "preact/hooks";
import { useMusette, toggleChosen, bookRides, rideDetail } from "../lib/store";
import { todayIso, mondayOf, addDays, dayLong, dateLabel, shortDate } from "../lib/dates";
import { FEELS } from "../lib/day";
import { real } from "../lib/analysis";
import { Head } from "../components/Head";
import { Ring, useToast } from "../components/Sheet";

export function Review() {
  const s = useMusette();
  const today = todayIso();
  // Friday to Sunday reviews this week; Monday to Thursday reviews last week.
  const dw = new Date(`${today}T12:00:00Z`).getUTCDay();
  const thisMon = dw >= 5 || dw === 0 ? mondayOf(today) : addDays(mondayOf(today), -7);
  const nextMon = addDays(thisMon, 7);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const { show, node: toast } = useToast();
  const ramp = s.ramp.find((r) => r.w === thisMon), nramp = s.ramp.find((r) => r.w === nextMon);
  const rides = s.rides.filter(real).filter((r) => r.d >= thisMon && r.d <= addDays(thisMon, 6)).sort((a, b) => (a.d < b.d ? -1 : 1));
  const km = Math.round(rides.reduce((a, r) => a + r.km, 0));
  const target = ramp?.rides ?? (ramp?.km ? null : 3);
  const [heads, setHeads] = useState<Record<string, string>>({});
  useEffect(() => { (async () => { const h: Record<string, string> = {}; for (const r of rides) if (r.id) { const x = await rideDetail(r.id); if (x?.writeup) h[r.id] = x.writeup.headline; } setHeads(h); })(); }, [rides.length]);
  const next = s.plan.find((p) => p.monday === nextMon);
  const proposed = next?.days.filter((d) => d.type === "ride" && d.status !== "cancelled") ?? [];
  const chosen = proposed.filter((d) => d.status === "ticked");
  const notes = s.coach?.notes && Date.now() - Date.parse(s.coach.notes.at) < 3 * 86400000 ? s.coach.notes.text : null;
  const route = (id?: string) => s.routes.find((r) => r.id === id)?.n ?? "City loop";

  return (
    <main class="screen signal">
      <Head right={`Review · ${shortDate(thisMon)}`} />
      <div class="review-steps" role="tablist" aria-label="Review steps">
        {["This week", "Next week", "Book"].map((t, i) => <button type="button" role="tab" aria-selected={i === step} class={i < step ? "done" : i === step ? "on" : ""} onClick={() => setStep(i)}><span class="label">{t}</span></button>)}
      </div>

      {step === 0 && (
        <section class="stack" style="margin-top: var(--space-4)">
          {!ramp ? (
            <div class="card"><span class="label amber">Week of {shortDate(thisMon)}</span><span class="ask">{km} km</span><span class="soft">Before the plan began. {rides.length ? `${rides.length} ${rides.length === 1 ? "ride" : "rides"}, measured by Strava.` : "No rides on Strava."}</span></div>
          ) : <div class="card week-head">
            <Ring done={target ? rides.filter((r) => r.km >= 15).length : km} of={target ?? ramp?.km ?? 1} segments={!!target}>
              <span class="stat-n">{target ? rides.filter((r) => r.km >= 15).length : km}</span>
              <span class="label">of {target ?? ramp?.km}</span>
            </Ring>
            <div class="week-ask">
              <span class="label amber">Week of {shortDate(thisMon)}</span>
              <span class="ask">{km} km</span>
              <span class="soft">{rides.length ? `${rides.length} ${rides.length === 1 ? "ride" : "rides"}, measured by Strava.` : "No rides on Strava this week."}</span>
            </div>
          </div>}
          {rides.map((r) => (
            <a class="card" href={`/rides/${r.id ?? r.d}`}>
              <div class="row-between"><span class="label">{dateLabel(r.d)}</span><span class="label">{s.log?.feel?.[r.d] ? FEELS.find((f) => f.id === s.log.feel[r.d])?.label : "No feel logged"}</span></div>
              <span class="note-title">{(r.id && heads[r.id]) || r.name || `${r.km} km`}</span>
              <span class="soft">{r.km} km · {r.m} m</span>
            </a>
          ))}
          {notes && <div class="card coach-card"><span class="label amber">From the coach</span><span class="soft prose-s">{notes}</span></div>}
          <button type="button" class="pill wide" onClick={() => setStep(1)}>Next: the week of {shortDate(nextMon)}</button>
        </section>
      )}

      {step === 1 && (
        <section class="stack" style="margin-top: var(--space-4)">
          <div class="card">
            <div class="row-between"><span class="label amber">Week of {shortDate(nextMon)}</span><span class="label">{nramp?.rides ? `${nramp.rides} rides` : nramp?.km ? `${nramp.km} km` : ""}</span></div>
            {nramp?.note && <span class="soft">{nramp.note}.</span>}
            {!next && <span class="soft">Not built yet. The Friday build at 17:00 makes it from your diary, or build it in Week.</span>}
            {proposed.map((d) => {
              const on = d.status === "ticked" || d.status === "booked";
              return (
                <button type="button" class={`review-day${on ? " on" : ""}`} role="checkbox" aria-checked={on} disabled={d.status === "booked"} onClick={() => toggleChosen(d.date)}>
                  <span class="box" aria-hidden="true">{on && <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.2l2.2 2.2L9.5 3.6" /></svg>}</span>
                  <span class="stepper-text"><span class="note-title">{dayLong(d.date)} · {d.start}</span><span class="label">{route(d.route)}{d.status === "booked" ? " · in the diary" : ""}</span></span>
                </button>
              );
            })}
          </div>
          {next && <a class="text-btn" href={`/week?w=next`}>Change times in Week</a>}
          <button type="button" class="pill wide" onClick={() => setStep(2)}>Next: book {chosen.length ? `${chosen.length} ${chosen.length === 1 ? "ride" : "rides"}` : "the week"}</button>
        </section>
      )}

      {step === 2 && (
        <section class="stack" style="margin-top: var(--space-4)">
          <div class="card">
            <span class="label ink">Going in the diary</span>
            {chosen.length ? chosen.map((d) => <div class="row-between"><span class="note-title">{dayLong(d.date)} {d.start}</span><span class="label">{route(d.route)}</span></div>) : <span class="soft">Nothing chosen. Go back a step and tick the rides you'll do.</span>}
          </div>
          <button type="button" class="pill wide" disabled={!chosen.length || busy} onClick={async () => { setBusy(true); show(await bookRides(chosen.map((d) => d.date))); setBusy(false); }}>
            {busy ? "Putting them in the diary" : chosen.length ? `Book ${chosen.length === 1 ? dayLong(chosen[0].date) : `${chosen.length} rides`}` : "Book"}
          </button>
          <a class="text-btn" href="/week?w=next">Open next week</a>
        </section>
      )}
      <div class="dock-wrap">{toast}</div>
    </main>
  );
}
